// src/Dashboard/pages/teacher/Events/utils/eventsDashboardUtils.js

import formatDate from "../../../../../utils/formatDate";
import { toAbsoluteEventPhotoUrl } from "../../../../../services/eventPhotoServices";

export const LEVEL_OPTIONS = [
  "Club",
  "Départemental",
  "Régional",
  "National",
  "International",
];

export const CATEGORY_OPTIONS = [
  "Eveil",
  "Mini-Poussin(e)",
  "Poussin(e)",
  "Benjamin(e)",
  "Minime",
  "Cadet(te)",
  "Junior",
  "Sénior",
  "Vétéran",
];

export const BASE_TYPE_OPTIONS = [
  "Compétition",
  "Animation",
  "Stage & Regroupement Technique",
  "Passage de Grade",
  "Entraînement Départemental",
  "Stage Sportif Départemental",
  "Tournoi Interclubs",
  "Cours commun Club",
  "Stage Club",
  "Autre",
];

export function getTypeOptions(shortnameClub = "") {
  const clubName = shortnameClub || "Votre Club";

  return [...new Set([clubName, ...BASE_TYPE_OPTIONS])];
}

export function normalizeString(value) {
  return String(value || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim();
}

export function getEventDate(event) {
  const date = new Date(event?.start);
  return Number.isNaN(date.getTime()) ? null : date;
}

export function isPastEvent(event) {
  const eventDate = getEventDate(event);

  if (!eventDate) return false;

  const eventDay = new Date(eventDate);
  eventDay.setHours(0, 0, 0, 0);

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  return eventDay < today;
}

export function isUpcomingEvent(event) {
  return !isPastEvent(event);
}

export function formatEventDate(event) {
  if (!event?.start) return "—";

  return formatDate(event.start);
}

export function parseEventCategories(cateEvent) {
  if (Array.isArray(cateEvent)) {
    return cateEvent.map((item) => String(item).trim()).filter(Boolean);
  }

  return String(cateEvent || "")
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);
}

export function getEventPhotoSrc(event) {
  return toAbsoluteEventPhotoUrl(event?.photo_url) || "/img/no-image.jpg";
}

export function dateToInputValue(value) {
  if (!value) return "";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) return "";

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
}

export function normalizeEventForForm(event) {
  return {
    titre: event?.titre || "",
    description: event?.description || "",
    start: dateToInputValue(event?.start),
    end: dateToInputValue(event?.end),
    type_event: event?.type_event || "",
    level_event: event?.level_event || "",
    cate_event: parseEventCategories(event?.cate_event),
    lieu_event: event?.lieu_event || "",
    horaire_event: event?.horaire_event || "",
    photo_url: event?.photo_url || "",
    agenda_url: event?.agenda_url || "",
    is_active: Boolean(event?.is_active ?? true),
    can_register: Number(event?.can_register ?? 1),
  };
}

export function getEmptyEventForm(shortnameClub = "") {
  return {
    titre: "",
    description: "",
    start: "",
    end: "",
    type_event: shortnameClub || "",
    level_event: "Club",
    cate_event: [],
    lieu_event: "",
    horaire_event: "",
    photo_url: "",
    agenda_url: "",
    is_active: true,
    can_register: 1,
  };
}

export function buildEventPayload(formData) {
  return {
    titre: String(formData.titre || "").trim(),
    description: String(formData.description || "").trim() || null,
    start: formData.start || null,
    end: formData.end || formData.start || null,
    type_event: formData.type_event || null,
    level_event: formData.level_event || null,
    cate_event: Array.isArray(formData.cate_event)
      ? formData.cate_event.join(",")
      : String(formData.cate_event || ""),
    lieu_event: String(formData.lieu_event || "").trim() || null,
    horaire_event: formData.horaire_event || null,
    photo_url: String(formData.photo_url || "").trim() || null,
    agenda_url: String(formData.agenda_url || "").trim() || null,
    is_active: !!formData.is_active,
    can_register: Number(formData.can_register ?? 1),
  };
}

export function filterEvents(events = [], filters = {}) {
  const query = normalizeString(filters.query);
  const now = new Date();
  now.setHours(0, 0, 0, 0);

  return events.filter((event) => {
    const eventDate = getEventDate(event);
    const eventDay = eventDate ? new Date(eventDate) : null;

    if (eventDay) {
      eventDay.setHours(0, 0, 0, 0);
    }

    const categories = parseEventCategories(event.cate_event);

    const matchesDate =
      filters.dateStatus === "all" ||
      (filters.dateStatus === "upcoming" && (!eventDay || eventDay >= now)) ||
      (filters.dateStatus === "past" && eventDay && eventDay < now);

    const matchesType =
      filters.type === "All" || event.type_event === filters.type;

    const matchesLevel =
      filters.level === "All" || event.level_event === filters.level;

    const matchesCategory =
      filters.category === "All" || categories.includes(filters.category);

    const matchesVisibility =
      filters.visibility === "All" ||
      (filters.visibility === "visible" && !!event.is_active) ||
      (filters.visibility === "hidden" && !event.is_active);

    const registrationValue = Number(event.can_register ?? 1);
    const matchesRegistration =
      filters.registration === "All" ||
      (filters.registration === "open" && registrationValue === 1) ||
      (filters.registration === "closed" && registrationValue === 0);

    const searchable = normalizeString(
      [
        event.titre,
        event.description,
        event.type_event,
        event.level_event,
        event.cate_event,
        event.lieu_event,
      ]
        .filter(Boolean)
        .join(" ")
    );

    const matchesQuery = !query || searchable.includes(query);

    return (
      matchesDate &&
      matchesType &&
      matchesLevel &&
      matchesCategory &&
      matchesVisibility &&
      matchesRegistration &&
      matchesQuery
    );
  });
}

export function sortEvents(events = [], sort = "date_asc") {
  return [...events].sort((a, b) => {
    const dateA = getEventDate(a)?.getTime() || 0;
    const dateB = getEventDate(b)?.getTime() || 0;

    if (sort === "date_desc") return dateB - dateA;

    if (sort === "title_asc") {
      return String(a.titre || "").localeCompare(String(b.titre || ""), "fr", {
        sensitivity: "base",
      });
    }

    if (sort === "type_asc") {
      return String(a.type_event || "").localeCompare(
        String(b.type_event || ""),
        "fr",
        { sensitivity: "base" }
      );
    }

    return dateA - dateB;
  });
}

export function paginateEvents(events = [], page = 1, pageSize = 10) {
  const start = (page - 1) * pageSize;

  return events.slice(start, start + pageSize);
}

export function calculateEventsStats(events = [], filteredEvents = []) {
  const upcomingCount = events.filter(isUpcomingEvent).length;
  const pastCount = events.filter(isPastEvent).length;
  const visibleCount = events.filter((event) => !!event.is_active).length;
  const hiddenCount = events.length - visibleCount;
  const openRegistrationCount = events.filter(
    (event) => Number(event.can_register ?? 1) === 1 && !isPastEvent(event)
  ).length;

  return {
    totalEvents: events.length,
    visibleEvents: filteredEvents.length,
    upcomingCount,
    pastCount,
    visibleCount,
    hiddenCount,
    openRegistrationCount,
  };
}

export function asExcelDateOrEmpty(value) {
  if (!value) return undefined;

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) return undefined;
  if (date.getFullYear() < 1901) return undefined;

  return date;
}

export async function exportEvents(events = []) {
  if (!events.length) {
    throw new Error("Aucun événement à exporter.");
  }

  const columns = [
    "id",
    "titre",
    "description",
    "start",
    "end",
    "type_event",
    "level_event",
    "cate_event",
    "lieu_event",
    "horaire_event",
    "photo_url",
    "agenda_url",
    "is_active",
    "can_register",
  ];

  const rows = events.map((event) => ({
    id: event?.id ?? "",
    titre: event?.titre ?? "",
    description: event?.description ?? "",
    start: asExcelDateOrEmpty(event?.start),
    end: asExcelDateOrEmpty(event?.end),
    type_event: event?.type_event ?? "",
    level_event: event?.level_event ?? "",
    cate_event: event?.cate_event ?? "",
    lieu_event: event?.lieu_event ?? "",
    horaire_event: event?.horaire_event ?? "",
    photo_url: event?.photo_url ?? "",
    agenda_url: event?.agenda_url ?? "",
    is_active: String(!!event?.is_active),
    can_register: String(Number(event?.can_register ?? 1)),
  }));

  const filenameDate = new Date().toISOString().slice(0, 10);

  try {
    const XLSX = await import("xlsx");
    const ws = XLSX.utils.json_to_sheet(rows, {
      header: columns,
      cellDates: true,
    });

    ws["!cols"] = [
      { wch: 6 },
      { wch: 34 },
      { wch: 44 },
      { wch: 12 },
      { wch: 12 },
      { wch: 26 },
      { wch: 18 },
      { wch: 34 },
      { wch: 22 },
      { wch: 38 },
      { wch: 42 },
      { wch: 42 },
      { wch: 12 },
      { wch: 14 },
    ];

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "evenements");
    const output = XLSX.write(wb, { bookType: "xlsx", type: "array" });

    const blob = new Blob([output], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");

    link.href = url;
    link.download = `evenements_filtres_${filenameDate}.xlsx`;
    link.click();

    URL.revokeObjectURL(url);
  } catch {
    const csvHeaders = columns.join(",");

    const toCsvValue = (value) => {
      if (value instanceof Date && !Number.isNaN(value.getTime())) {
        return value.toISOString().slice(0, 10);
      }

      const safe = String(value ?? "");

      return `"${safe.replace(/"/g, '""')}"`;
    };

    const csvBody = rows
      .map((row) => columns.map((key) => toCsvValue(row[key])).join(","))
      .join("\n");

    const csv = `\ufeff${csvHeaders}\n${csvBody}\n`;
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });

    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");

    link.href = url;
    link.download = `evenements_filtres_${filenameDate}.csv`;
    link.click();

    URL.revokeObjectURL(url);
  }
}